import { BottomSheet, Card, Text, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { alpha, Box, CardContent, DialogContent, RadioGroup, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { RadioField } from "@src/appV2/lib/FormFields/RadioField/RadioField";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { useCallback, useState } from "react";

import goldCoinImage from "./assets/images/goldCoin.png";
import {
  GUARANTEED_PAY_BOTTOM_SHEET_GRADIENT,
  GUARANTEED_PAY_PRICING_RESPONSE_LABELS,
  GUARANTEED_PAY_SURVEY_OPTIONS,
  type GuaranteedPayPricingResponse,
} from "./constants";

interface GuaranteedPayPricingSurveyBottomSheetProps {
  modalState: UseModalState;
  workerId: string;
  onSubmit: (response: GuaranteedPayPricingResponse) => void;
}

export function GuaranteedPayPricingSurveyBottomSheet(
  props: GuaranteedPayPricingSurveyBottomSheetProps
) {
  const { modalState, workerId, onSubmit } = props;
  const [selectedResponse, setSelectedResponse] = useState<GuaranteedPayPricingResponse | "">("");

  useLogEffect(
    APP_V2_USER_EVENTS.GUARANTEED_PAY_PRICING_SURVEY_BOTTOM_SHEET_VIEWED,
    { workerId },
    { enabled: modalState.modalIsOpen }
  );

  const handleConfirm = useCallback(() => {
    if (!selectedResponse) {
      return;
    }

    logEvent(APP_V2_USER_EVENTS.GUARANTEED_PAY_PRICING_SURVEY_SUBMITTED, {
      workerId,
      response: selectedResponse,
    });

    onSubmit(selectedResponse);
    modalState.closeModal();
  }, [selectedResponse, workerId, onSubmit, modalState]);

  function handleDismiss(): void {
    logEvent(APP_V2_USER_EVENTS.GUARANTEED_PAY_PRICING_SURVEY_BOTTOM_SHEET_DISMISSED, {
      workerId,
    });
  }

  const handleResponseChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedResponse(event.target.value as GuaranteedPayPricingResponse);
  }, []);

  return (
    <BottomSheet
      modalState={modalState}
      paperSx={{
        minHeight: "90%",
        background: GUARANTEED_PAY_BOTTOM_SHEET_GRADIENT,
        overflow: "hidden",
      }}
      header={
        <BottomSheetHeader
          onClose={() => {
            handleDismiss();
            modalState.closeModal();
          }}
        />
      }
      footer={
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!selectedResponse}
            onClick={handleConfirm}
          >
            Confirm Selection
          </Button>
        </DialogFooter>
      }
      onClose={() => {
        handleDismiss();
      }}
    >
      <Box
        component="img"
        src={goldCoinImage}
        alt=""
        sx={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: "70%",
          maxWidth: 320,
          aspectRatio: "1 / 1",
          objectFit: "contain",
          pointerEvents: "none",
          transform: "translate(20%, -25%)",
          opacity: 0.75,
        }}
      />
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", flex: 1, position: "relative" }}
      >
        <Stack gap={4}>
          <Text
            semibold
            variant="h2"
            sx={(theme) => ({ textAlign: "center", color: theme.palette.text.primary })}
          >
            Stop losing money to cancellations
          </Text>
          <Text
            variant="body1"
            sx={(theme) => ({ textAlign: "center", color: theme.palette.text.secondary })}
          >
            We&apos;re testing a new feature to protect your income. For a small fee, if a facility
            cancels within 24 hours of your shift, CBH pays you the full amount.
          </Text>
        </Stack>
        <Stack sx={{ flex: 1, justifyContent: "center" }}>
          <Card
            variant="tertiary"
            sx={(theme) => ({
              ...(isDefined(theme.palette.background.tertiary) && {
                backgroundColor: alpha(theme.palette.background.tertiary, 0.8),
              }),
              marginY: 8,
            })}
          >
            <CardContent>
              <Stack spacing={3}>
                <Text semibold variant="h5" paddingBottom={2}>
                  What&apos;s the most you would be willing to pay for Guaranteed Pay?
                </Text>
                <Text
                  semibold
                  variant="body1"
                  sx={(theme) => ({ color: theme.palette.intent?.warning?.text })}
                >
                  These rates are per shift:
                </Text>
                <RadioGroup value={selectedResponse} onChange={handleResponseChange}>
                  {GUARANTEED_PAY_SURVEY_OPTIONS.map((response) => (
                    <RadioField
                      key={response}
                      value={response}
                      label={<Text>{GUARANTEED_PAY_PRICING_RESPONSE_LABELS[response]}</Text>}
                    />
                  ))}
                </RadioGroup>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
